import React, { useRef } from 'react';
import { string } from 'prop-types';
import TextInput from './TextInput';
import TextLabel from './TextLabel';
import TextInfo from './TextInfo';
import useUniqueId from '../../hooks/useUniqueId';
import useStyles from '../../hooks/useStyles';
import useTextValidation from '../../hooks/useTextValidation';
import { defaultProps, propTypes } from './inputPropTypes';

TextField.propTypes = {
	...propTypes,
	type: string
};

TextField.defaultProps = {
	type: 'text',
	...defaultProps
};

export default function TextField({
	className,
	onValidityChange,
	onBlur: onBlurProp,
	onKeyDown: onKeyDownProp,
	overrideValidation,
	required,
	pattern,
	maxLength,
	minLength,
	label,
	info,
	type,
	value,
	innerCss,
	onRef,
	disableValidation,
	requiredMessage,
	errorMessage: errorMessageProp,
	...rest
}) {
	const ref = useRef();
	const id = useUniqueId('text-field');
	const classes = useStyles(styles);
	const [validity, errorMessage, onBlur, onKeyDown, enableValidation] = useTextValidation({
		onBlur: onBlurProp,
		onKeyDown: onKeyDownProp,
		onValidityChange,
		disableValidation,
		value,
		ref,
		overrideValidation,
		errorMessage: errorMessageProp,
		requiredMessage,
		dependencies: { pattern, required, maxLength, minLength }
	});

	return (
		<div css={[classes.root, innerCss.root]} className={className}>
			{label && <TextLabel htmlFor={id}>{label}</TextLabel>}
			<div css={classes.inputWrapper}>
				<TextInput
					css={[classes.input, innerCss.input]}
					className="full-width"
					type={type}
					id={id}
					validity={validity.valid}
					pattern={pattern ? pattern : undefined}
					maxLength={maxLength}
					minLength={minLength}
					required={required}
					onBlur={onBlur}
					onKeyDown={onKeyDown}
					value={value}
					onRef={node => {
						if (typeof ref === 'function') onRef(ref);
						else onRef.current = node;
						ref.current = node;
					}}
					{...rest}
				/>
			</div>
			{(info || (errorMessage && enableValidation)) && (
				<TextInfo validity={validity.valid}>{info || errorMessage}</TextInfo>
			)}
		</div>
	);
}

function styles({ css }) {
	return {
		root: css`
			display: flex;
			flex-direction: column;
		`,
		inputWrapper: css`
			position: relative;
		`,
		input: css`
			width: 100%;
		`
	};
}
